import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  encodeAbiParameters,
  EncodeAbiParametersReturnType,
  keccak256,
} from 'viem';
import { signMessage } from 'viem/accounts';
import { z } from 'zod';

import WithdrawToast from '@/components/toast/withdraw-toast';
import { getTransfers } from '@/features/account/api/get-transfers';
import { useAccountStore } from '@/store/use-account-store';
import { COLLATERAL_DECIMALS } from '@/store/use-markets-store';
import { useTransfersStore } from '@/store/use-transfers-store';
import { EventType } from '@/types';
import { Entity } from '@/types/api';
import { ErrorCode, ErrorMsgs, OrderStatus } from '@/types/enums';
import { getUSDCAddress } from '@/utils/addresses';
import { sendEvent } from '@/utils/analytics';
import {
  handleTransferForStore,
  transferResponseToTransfer,
} from '@/utils/handle-store-data';
import { bigIntToDecimalStr, bigIntToNum } from '@/utils/value-format';

import { privateApi, privateV2Api } from '../api-clients/rest-client';
import { getChainDecimals } from '../wagmi/config';

const createWithdrawInputSchema = z.object({
  size: z.bigint(),
  chainId: z.number(),
  execute: z.boolean(),
  recipient: z.string().optional(),
});

type CreateWithdrawParams = z.infer<typeof createWithdrawInputSchema>;

type CreateWithdrawResponse = Entity<{
  id: string;
  status: string; // NEW
}>;

const createWithdraw = async (
  data: CreateWithdrawParams,
): Promise<CreateWithdrawResponse> => {
  if (!data.recipient) {
    throw new Error('Invalid recipient');
  }

  const { address, getAuthParams, nextNonce, getAccurateTime } =
    useAccountStore.getState();

  if (!address) {
    throw new Error('Account not selected');
  }

  const authParams = getAuthParams();

  if (!authParams?.signingKey) {
    throw new Error('No signing key found');
  }

  const nonce = nextNonce();
  const token = getUSDCAddress(data.chainId);

  let promise: Promise<CreateWithdrawResponse>;

  if (authParams.accountGroup === 2) {
    promise = privateV2Api.post('/v2/withdraw', {
      amount: bigIntToDecimalStr(data.size, getChainDecimals(data.chainId)),
      chain_id: data.chainId,
      execute: data.execute,
      nonce,
      to_address: data.recipient,
      token,
    });
  } else {
    const time = getAccurateTime();

    let encodedOrder: EncodeAbiParametersReturnType;

    try {
      encodedOrder = encodeAbiParameters(
        [
          { name: 'time', type: 'uint256' },
          { name: 'nonce', type: 'uint256' },
          { name: 'isDeposit', type: 'bool' },
          { name: 'account', type: 'address' },
          { name: 'recipient', type: 'address' },
          { name: 'token', type: 'address' },
          { name: 'size', type: 'uint256' },
          { name: 'chainId', type: 'uint256' },
        ],
        [
          BigInt(time),
          BigInt(nonce),
          false,
          authParams.address as `0x${string}`,
          data.recipient as `0x${string}`,
          token,
          data.size,
          BigInt(data.chainId),
        ],
      );
    } catch (err) {
      toast.error(
        'message' in (err as Error)
          ? (err as Error).message.split('.')[0] + '.'
          : 'Something went wrong. Please check your input and try again.',
      );
      return Promise.reject();
    }

    const signature = await signMessage({
      message: { raw: keccak256(encodedOrder) },
      privateKey: authParams.signingKey,
    });

    const body = {
      order: {
        ...data,
        size: Number(data.size),
        nonce,
        time,
      },
      execute: data.execute,
      signature,
    };

    promise = privateApi.post('/transfer/withdraw', body);
  }

  const shouldWaitValidation =
    !data.execute && bigIntToNum(data.size, COLLATERAL_DECIMALS) < 50000;

  const toastPromise = promise.then((res) => {
    const { id: orderId } = res;
    sendEvent(EventType.WITHDRAW_INITIATED, {
      amount: bigIntToNum(data.size, COLLATERAL_DECIMALS),
    });
    return new Promise((resolve, reject) => {
      // eslint-disable-next-line prefer-const
      let subscriberTimeoutId: NodeJS.Timeout;
      // eslint-disable-next-line prefer-const
      let pollIntervalId: NodeJS.Timeout;
      // eslint-disable-next-line prefer-const
      let unsubscribe: () => void;

      const cleanup = () => {
        clearTimeout(subscriberTimeoutId);
        clearInterval(pollIntervalId);
        unsubscribe();
      };

      unsubscribe = useTransfersStore.subscribe((state, _prevState) => {
        const transfers = state.transfers[address] ?? {};
        if (orderId in transfers) {
          cleanup();
          if (
            transfers[orderId].status === OrderStatus.NEW ||
            transfers[orderId].status === OrderStatus.PROCESSING
          ) {
            if (shouldWaitValidation)
              toast.loading('Waiting for order validation...');
            resolve(transfers[orderId]);
          } else if (transfers[orderId].status === OrderStatus.REJECTED) {
            reject(transfers[orderId].code as ErrorCode);
          }
        }
      });

      subscriberTimeoutId = setTimeout(() => {
        cleanup();
        reject(ErrorCode.INTERNAL_CREATE_ORDER_TIMEOUT);
      }, 30_000); // 30 seconds timeout

      pollIntervalId = setInterval(() => {
        getTransfers({ id: orderId }).then((res) =>
          res.forEach((data) => {
            const transfer = transferResponseToTransfer(data);
            handleTransferForStore(transfer);
          }),
        );
      }, 2_000);
    });
  });

  toast.promise(toastPromise, {
    loading: 'Requesting withdrawal...',
    success: WithdrawToast,
    error: (error) => `Withdraw failed: ${ErrorMsgs[error as ErrorCode]}`,
  });

  return promise;
};

export default function useCreateWithdraw() {
  return useMutation({
    mutationFn: (params: CreateWithdrawParams) => createWithdraw(params),
  });
}
